import type { ReactNode } from 'react';

/**
 * The one backdrop every dialog in the admin sits on, and the one close
 * button they share.
 *
 * ⚠️ **One colour, dark, whatever the theme.** Three dialogs had three
 * backdrops: the shared `Modal` an `rgba` near-black, the Topics dialog an
 * `oklch` of its own, and the translate modal `var(--bg)` at 72% — which on
 * the light theme is *white*, and anh Duy saw a white sheet behind
 * "Translating with AI" where the two-factor modal beside it darkened the
 * page. A backdrop's job is to say "the page is behind this now", and it
 * says it the same way in both themes: the dialog itself carries the theme,
 * the sheet behind it does not.
 *
 * Closing on a click that lands on the sheet itself, and not on the dialog
 * — `target === currentTarget` — is here so no dialog reimplements it with
 * one condition missing. Escape is not here: the dialogs disagree about it
 * on purpose (a run in flight ignores it; a form does not).
 */
export function Overlay({
  onBackdropClick,
  children,
  z = 40,
}: {
  onBackdropClick?: () => void;
  children: ReactNode;
  /** Above the sticky header (5) and bars (4); the translate modal asks for more. */
  z?: number;
}) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center overflow-y-auto px-5 py-6"
      style={{
        zIndex: z,
        background: 'oklch(0.08 0.005 260 / 0.72)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onBackdropClick?.();
      }}
    >
      {children}
    </div>
  );
}

/**
 * The × in a dialog's corner. 28px square with a drawn cross, where it was
 * a 15px "×" glyph in a text button — a target the size of a comma, which
 * anh Duy asked to be made pressable.
 */
export function CloseButton({ onClick, label = 'Close' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="btn-quiet focus-ring ml-auto flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-md"
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <path d="M3 3l8 8M11 3l-8 8" />
      </svg>
    </button>
  );
}
